import { useState } from 'react'
import type { ChangeEvent } from 'react'
import { Icon } from '@/shared/ui/Icon'
import type { UploadFormErrors, UploadFormValues } from './types'

interface Props {
  values: UploadFormValues
  errors: UploadFormErrors
  disabled?: boolean
  onChange: (field: keyof UploadFormValues, value: string) => void
}

/**
 * Metadata fields shared by the upload form.
 *
 * Design notes (Step 7.9B):
 *   - ``tenant_id`` is intentionally NOT editable here — the page derives it
 *     from the authenticated organization (cookie-only auth model).
 *   - Labels are PT, human-readable. Helper text references multiple
 *     verticals (patient/client/case/company/project) so the same form makes
 *     sense for clinics, law firms, consultants and internal KBs without UI
 *     work per tenant.
 *   - Group + document_id fields live under "Metadados avançados" — they
 *     remain visible (one click away) so local testing stays easy.
 *
 * Future (Step ≥ 7.10 plug-and-play tenant templates):
 *   Replace the static placeholders/helper text with values resolved from a
 *   per-tenant preset (clinic / law firm / consulting / internal KB / sales
 *   enablement). The component contract stays unchanged.
 */
export function DocumentMetadataForm({
  values,
  errors,
  disabled,
  onChange,
}: Props) {
  // Auto-expand the advanced block whenever any of its fields has user input
  // or an error attached — local testing must never hide a failing field.
  const hasAdvancedInput =
    values.groupType.trim().length > 0 ||
    values.groupId.trim().length > 0 ||
    values.documentId.trim().length > 0 ||
    values.groupDisplayName.trim().length > 0 ||
    values.title.trim().length > 0
  const hasAdvancedError = Boolean(
    errors.groupType ||
      errors.groupId ||
      errors.documentId ||
      errors.groupDisplayName ||
      errors.title,
  )
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const expanded = advancedOpen || hasAdvancedInput || hasAdvancedError

  const handle =
    (field: keyof UploadFormValues) =>
    (event: ChangeEvent<HTMLInputElement>) =>
      onChange(field, event.target.value)

  return (
    <div className="doc-form">
      <div className="doc-form-grid">
        <Field
          label="Tipo de entidade"
          required
          testId="upload-entity-type"
          value={values.entityType}
          error={errors.entityType}
          placeholder="patient"
          helper="Ex.: patient, client, case, company, project"
          disabled={disabled}
          onChange={handle('entityType')}
        />
        <Field
          label="ID da entidade"
          required
          testId="upload-entity-id"
          value={values.entityId}
          error={errors.entityId}
          placeholder="paciente_ana"
          helper="Ex.: paciente_ana, cliente_acme, processo_001"
          disabled={disabled}
          onChange={handle('entityId')}
        />
        <Field
          label="Nome para exibição"
          value={values.entityDisplayName}
          error={errors.entityDisplayName}
          placeholder="Ana Ribeiro"
          helper="Opcional — usado na UI no lugar do ID técnico (Step 9.5)."
          disabled={disabled}
          onChange={handle('entityDisplayName')}
          full
        />
      </div>

      <button
        type="button"
        className={`doc-form-advanced-toggle${expanded ? ' is-open' : ''}`}
        onClick={() => setAdvancedOpen((v) => !v)}
        disabled={disabled}
        aria-expanded={expanded}
        aria-controls="doc-form-advanced"
      >
        <Icon name="chev-down" />
        Metadados avançados
        <span className="doc-form-advanced-hint">
          grupo opcional · ID do documento editável · título amigável
        </span>
      </button>

      {expanded ? (
        <div id="doc-form-advanced" className="doc-form-grid doc-form-advanced">
          <Field
            label="Tipo de grupo"
            value={values.groupType}
            error={errors.groupType}
            placeholder="treatment"
            helper="Opcional. Ex.: treatment, lawsuit, contract, project"
            disabled={disabled}
            onChange={handle('groupType')}
          />
          <Field
            label="ID do grupo"
            value={values.groupId}
            error={errors.groupId}
            placeholder="tratamento_joelho"
            helper="Opcional. Ex.: tratamento_joelho, caso_trabalhista"
            disabled={disabled}
            onChange={handle('groupId')}
          />
          <Field
            label="Nome do grupo"
            value={values.groupDisplayName}
            error={errors.groupDisplayName}
            placeholder="Reabilitação de joelho direito"
            helper="Opcional — exibido na UI no lugar do ID técnico."
            disabled={disabled}
            onChange={handle('groupDisplayName')}
            full
          />
          <Field
            label="Título amigável"
            value={values.title}
            error={errors.title}
            placeholder="Avaliação fisioterapêutica inicial"
            helper="Opcional — substitui o nome do arquivo em listagens e citações."
            disabled={disabled}
            onChange={handle('title')}
            full
          />
          <Field
            label="ID do documento"
            value={values.documentId}
            error={errors.documentId}
            placeholder="relatorio_luiza_cervical_2026"
            helper="Gerado automaticamente a partir do nome do arquivo. Edite para sobrescrever."
            disabled={disabled}
            onChange={handle('documentId')}
            full
            mono
          />
        </div>
      ) : null}
    </div>
  )
}

interface FieldProps {
  label: string
  value: string
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  required?: boolean
  helper?: string
  error?: string
  disabled?: boolean
  full?: boolean
  mono?: boolean
  testId?: string
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
  helper,
  error,
  disabled,
  full,
  mono,
  testId,
}: FieldProps) {
  return (
    <label className={`doc-field${full ? ' doc-field-full' : ''}`}>
      <span className="doc-field-label">
        {label}
        {required ? (
          <em className="doc-field-required" aria-hidden="true">*</em>
        ) : (
          <span className="doc-field-optional">opcional</span>
        )}
      </span>
      <input
        type="text"
        data-testid={testId}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        disabled={disabled}
        className={`doc-field-input${mono ? ' mono' : ''}${error ? ' has-error' : ''}`}
        spellCheck={false}
        autoComplete="off"
      />
      {helper && !error ? <span className="doc-field-helper">{helper}</span> : null}
      {error ? <span className="doc-field-error">{error}</span> : null}
    </label>
  )
}
